import React, { useEffect, useState, useContext } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import cn from "classnames";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PatientDataTable } from "./PatientDataTable";
import { TreatmentsAccordion } from "./TreatmentsAccordion";
import DeathModal from "./DeathModal";
import { Button, Loading, Icon, Input } from "components";
import { getPatient, editPatient } from "services/patients";
import { showError } from "utils/alerts";
import { useAuth } from "contexts/AuthContext";
import { useModal } from "hooks/useModal";

import {
  demographyAdapter,
  sampleAdapter,
  diagnosticAdapter,
  reportsAdapter,
} from "adapters/patients";
import "./index.scss";

export const Patient = () => {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState<PatientType>();
  const [patientCode, setPatientCode] = useState(patient?.codeOP || "");
  const [saveLoading, setSaveLoading] = useState(false);
  const { visible, toggleModal } = useModal();

  const { user } = useAuth();

  useEffect(() => {
    patientId && fetch();
  }, []);

  const fetch = async () => {
    if (patientId) {
      try {
        setLoading(true);
        const { data } = await getPatient(patientId);
        setPatient(data);
        setPatientCode(data.codeOP);
      } catch (error) {
        showError("There was an error trying to get patient data");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOnSavePatientCode: any = async () => {
    console.log({ patientCode });
    setSaveLoading(true);
    try {
      const patientId = patient?.id?.toString();
      if (patientId) {
        const res = await editPatient(patientId, { codeOP: +patientCode });
        setPatient({ ...patient, codeOP: +patientCode });
      }
    } catch (error: any) {
      console.log({ error });
      showError(error?.response?.data?.message);
    }
    setSaveLoading(false);
  };

  const handleOnShowDeathModal = () => {
    toggleModal();
  };

  const isBio = patient?.institution?.type === "BioBanco";

  return loading ? (
    <Loading />
  ) : patient ? (
    <>
      <div className="max-w-4xl mx-auto relative block">
        <div className="flex justify-between">
          <div className="flex items-center">
            <Button type="link" to="/patients" className="mr-4">
              <Icon name="BackArrowSmall" height={14} />
            </Button>
            <p className="text-xl">{patient.identification}</p>
          </div>
          <div className="flex items-center">
            {user.isAdmin ? (
              <>
                <input
                  value={patientCode}
                  className="py-2.5 text-grey w-32"
                  onChange={({ target }: any) => setPatientCode(target.value)}
                />
                <Button
                  handleClick={handleOnSavePatientCode}
                  className="edit-button hover:underline ml-4 w-content"
                  loading={saveLoading}
                >
                  + {t("savePatientCode")}
                </Button>
              </>
            ) : patient?.deathDate ? null : (
              <Button
                handleClick={handleOnShowDeathModal}
                background
                textColor="white"
                className="flex items-center"
              >
                + {t("death")}
              </Button>
            )}
          </div>
        </div>
        <Accordion allowZeroExpanded allowMultipleExpanded>
          <AccordionItem
            className="bg-white my-6"
            activeClassName="bg-white my-6 opened"
          >
            <AccordionItemHeading>
              <AccordionItemButton
                className={cn(
                  "border border-grey-light rounded-lg p-6 text-orange flex justify-between items-center"
                )}
              >
                01.- {t("demography")}
                <div className="flex items-center">
                  {!user?.isAdmin && (
                    <Button
                      type="link"
                      className="edit-button hover:underline mr-8"
                      textColor="blue"
                      to={`/edit-patient/${patient?.id}/demography`}
                    >
                      + {t("edit")}
                    </Button>
                  )}
                  <Icon className="opened-arrow" name="ArrowBottomSmall" />
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className="w-full border border-grey-light rounded-lg border-t-0 rounded-t-none">
              <PatientDataTable
                className="px-6"
                data={demographyAdapter(patient?.demography, isBio)}
              />
            </AccordionItemPanel>
          </AccordionItem>

          <AccordionItem
            className="bg-white mb-6"
            activeClassName="bg-white my-6 opened"
          >
            <AccordionItemHeading>
              <AccordionItemButton
                className={cn(
                  "border border-grey-light rounded-lg p-6 text-orange flex justify-between items-center"
                )}
              >
                02.- {t("preTreatmentSamples")}
                <div className="flex items-center">
                  {!user?.isAdmin && (
                    <Button
                      type="link"
                      className="edit-button hover:underline mr-8"
                      textColor="blue"
                      to={`/edit-patient/${patient?.id}/samples`}
                    >
                      + {t("edit")}
                    </Button>
                  )}
                  <Icon className="opened-arrow" name="ArrowBottomSmall" />
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className="w-full border border-grey-light rounded-lg border-t-0 rounded-t-none">
              {patient.samples?.length ? (
                <PatientDataTable
                  className="px-6"
                  data={sampleAdapter(patient?.samples[0])}
                />
              ) : (
                <p className="px-6">{t("noSamples")}</p>
              )}
            </AccordionItemPanel>
          </AccordionItem>

          <AccordionItem
            className="bg-white mb-6"
            activeClassName="bg-white my-6 opened"
          >
            <AccordionItemHeading>
              <AccordionItemButton
                className={cn(
                  "border border-grey-light rounded-lg p-6 text-orange flex justify-between items-center"
                )}
              >
                03.- {t("diagnostic")}
                <div className="flex items-center">
                  {!user?.isAdmin && (
                    <Button
                      type="link"
                      className="edit-button hover:underline mr-8"
                      textColor="blue"
                      to={`/edit-patient/${patient?.id}/diagnostic`}
                    >
                      + {t("edit")}
                    </Button>
                  )}
                  <Icon className="opened-arrow" name="ArrowBottomSmall" />
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className="w-full border border-grey-light rounded-lg border-t-0 rounded-t-none">
              {patient?.diagnostics?.length ? (
                <PatientDataTable
                  className="px-6"
                  data={diagnosticAdapter(patient.diagnostics[0], isBio)}
                />
              ) : (
                <p className="px-6">{t("noDiagnostics")}</p>
              )}
            </AccordionItemPanel>
          </AccordionItem>

          <AccordionItem
            className="bg-white mb-6"
            activeClassName="bg-white my-6 opened"
          >
            <AccordionItemHeading>
              <AccordionItemButton
                className={cn(
                  "border border-grey-light rounded-lg p-6 text-orange flex justify-between items-center"
                )}
              >
                04.- {t("treatments")}
                <div className="flex items-center">
                  {!user?.isAdmin && (
                    <Button
                      type="link"
                      className="edit-button hover:underline mr-8"
                      textColor="blue"
                      to={`/edit-patient/${patient?.id}/treatments`}
                    >
                      + {t("edit")}
                    </Button>
                  )}
                  <Icon className="opened-arrow" name="ArrowBottomSmall" />
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className="w-full border border-grey-light rounded-lg border-t-0 rounded-t-none">
              {patient.treatments?.length > 0 ? (
                patient.id && (
                  <TreatmentsAccordion
                    patientId={patient.id}
                    treatments={patient.treatments}
                  />
                )
              ) : (
                <p className="px-6">{t("noTreatments")}</p>
              )}
            </AccordionItemPanel>
          </AccordionItem>

          <AccordionItem
            className="bg-white mb-6"
            activeClassName="bg-white my-6 opened"
          >
            <AccordionItemHeading>
              <AccordionItemButton
                className={cn(
                  "border border-grey-light rounded-lg p-6 text-orange flex justify-between items-center"
                )}
              >
                05.- {t("laboratoryReport")}
                <div className="flex items-center">
                  <Button
                    type="link"
                    className="edit-button hover:underline mr-8"
                    textColor="blue"
                    to={`/edit-patient/${patient?.id}/lab-report`}
                  >
                    + {t("edit")}
                  </Button>
                  <Icon className="opened-arrow" name="ArrowBottomSmall" />
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className="w-full border border-grey-light rounded-lg border-t-0 rounded-t-none">
              {patient?.riskLevel && (
                <PatientDataTable data={{ riskLevel: patient.riskLevel }} />
              )}
              {/* @ts-ignore */}

              {patient?.reports?.length > 0 ? (
                <PatientDataTable
                  className="px-6"
                  data={reportsAdapter(patient.reports)}
                />
              ) : (
                <p className="px-6">{t("noInformation")}</p>
              )}
            </AccordionItemPanel>
          </AccordionItem>
          {patient?.deathDate && (
            <AccordionItem
              className="bg-white mb-6"
              activeClassName="bg-white my-6 opened"
            >
              <AccordionItemHeading>
                <AccordionItemButton
                  className={cn(
                    "border border-grey-light rounded-lg p-6 text-orange flex justify-between items-center"
                  )}
                >
                  06.- {t("death")}
                  <div className={cn("flex justify-between items-center")}>
                    {!user.isAdmin && (
                      <Button
                        type="link"
                        className="edit-button hover:underline mr-8"
                        textColor="blue"
                        handleClick={handleOnShowDeathModal}
                      >
                        + {t("change-death")}
                      </Button>
                    )}
                    <Icon className="opened-arrow" name="ArrowBottomSmall" />
                  </div>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel className="w-full border border-grey-light rounded-lg border-t-0 rounded-t-none">
                {patient?.deathDate && (
                  <PatientDataTable
                    data={{
                      deathDate: patient.deathDate,
                      deathCause: patient.deathCause,
                    }}
                  />
                )}
              </AccordionItemPanel>
            </AccordionItem>
          )}
        </Accordion>
      </div>
      <DeathModal
        patient={patient}
        setPatient={setPatient}
        visible={visible}
        toggle={toggleModal}
      />
    </>
  ) : (
    <div>Este paciente no existe</div>
  );
};
